import axios from "axios";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  ExperienceActionRequestBodyType,
  ExperienceActionRequestPathParamsType,
  ExperienceActionResponseBodyType,
  TabEnumFe,
  TabType,
} from "@intelligems/ig-types";
import {
  initExperimentFromDto,
  setExperienceTab,
} from "@src/store/slices/experiences";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  setConfetti,
  setIgnoreUpdateDetected,
} from "../../store/slices/global";
import { mpStartExperience } from "../../utils/mixpanel";
import { useConfiguration } from "../configurations/use-configuration";
import {
  experienceKeys,
  historyKeys,
  offerKeys,
  scheduleKeys,
} from "../queryKeys";
import { ApiRequest } from "../types/api";

import { experienceAction } from "./experience-action";

export const useStartExperience = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const shouldDuplicateProducts = useConfiguration<boolean>(
    "should_duplicate_products"
  );
  return useMutation<
    ExperienceActionResponseBodyType,
    Error,
    ApiRequest<
      ExperienceActionRequestBodyType,
      ExperienceActionRequestPathParamsType & {
        pausedAtTs: number | null;
        name: string;
      }
    >
  >({
    mutationKey: ["startExperience"],
    mutationFn: async (data) => experienceAction(data),
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 503) {
          toast.info("Starting Experience");
        } else {
          if (error.response?.data.error) {
            toast.error(error.response.data.error);
          } else {
            toast.error("Error Starting Experience");
          }
        }
      }
    },
    onMutate: () => {
      dispatch(setIgnoreUpdateDetected(true));
    },
    onSuccess: async (data, payload) => {
      await queryClient.invalidateQueries({
        queryKey:
          data.experience?.category === "experiment"
            ? experienceKeys.experimentList
            : experienceKeys.personalizationList,
      });
      void queryClient.invalidateQueries({
        queryKey: historyKeys.one(payload.pathParams.experienceId),
      });
      await queryClient.invalidateQueries({
        queryKey: experienceKeys.one(payload.pathParams.experienceId),
      });
      void queryClient.invalidateQueries({
        queryKey: scheduleKeys.one(payload.pathParams.experienceId),
      });
      void queryClient.invalidateQueries({
        queryKey: offerKeys.all,
      });
      dispatch(setConfetti(true));
      if (data.experience?.category === "experiment") {
        mpStartExperience({
          id: payload.pathParams.experienceId,
          name: payload.pathParams.name,
          pausedAtTs: payload.pathParams.pausedAtTs,
        });

        dispatch(
          initExperimentFromDto({
            experience: data.experience,
            testFlowType: data.experience.type,
            tab: (searchParams.get("tab") as TabType) || TabEnumFe.ANALYTICS,
          })
        );
        dispatch(setExperienceTab(TabEnumFe.ANALYTICS));
      }
    },
  });
};
